import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { getEnvironmentAccountId } from "@/lib/accountContext";
import { apiFetch } from "@/lib/api";
import { accountIdQueryKey } from "@/lib/authQueryCache";
import { useUserStore } from "@/store/userStore";
import type { AccountOutput } from "@/types";

interface UseAccountIdOptions {
  syncUserStore?: boolean;
}

export function useAccountId(accountId?: string, options?: UseAccountIdOptions) {
  const resolvedAccountId = accountId ?? getEnvironmentAccountId();
  const shouldSyncUserStore = options?.syncUserStore ?? false;
  const setUser = useUserStore((s) => s.setUser);
  const setAuthenticatedAccountId = useUserStore((s) => s.setAuthenticatedAccountId);

  const result = useQuery({
    queryKey: [...accountIdQueryKey, resolvedAccountId] as const,
    queryFn: () => apiFetch<AccountOutput>(`/v1/account/${resolvedAccountId}`),
    enabled: !!resolvedAccountId,
  });

  useEffect(() => {
    if (!result.data || !shouldSyncUserStore) {
      return;
    }

    setAuthenticatedAccountId(result.data.account_id);
    setUser({
      account_id: result.data.account_id,
      name: result.data.account_name,
      email: result.data.email,
    });
  }, [result.data, setAuthenticatedAccountId, setUser, shouldSyncUserStore]);

  return result;
}
